import React, { useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  Grid,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import CircleNotificationsIcon from "@mui/icons-material/CircleNotifications";
import CloseIcon from "@mui/icons-material/Close";
import GenericBreadCrums from "@components/Breadcrumbs";

const member = {
  name: "Rajesh Kumar",
  initials: "RK",
  firstName: "Rajesh",
  lastName: "Kumar",
  designation: "Senior Officer",
  status: "Active",
  assignedRole: "Nodal Officer",
  spocId: "SPOC-2024-0042",
  email: "rajesh.kumar@example.gov.in",
  phone: "+91 98765 43210",
  organisation: "Ministry of Urban Development",
  onboardedOn: "12 Jan 2024",
  onboardedDate: "January 12, 2024",
};

interface InfoFieldProps {
  label: string;
  value: string;
  isEmail?: boolean;
  locked?: boolean;
}

const InfoField: React.FC<InfoFieldProps> = ({ label, value, isEmail, locked }) => (
  <Box>
    <Typography sx={{ fontSize: "12px", color: "#757575", mb: 0.5 }}>
      {label}
    </Typography>
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      {isEmail && <EmailOutlinedIcon sx={{ fontSize: 15, color: "#9E9E9E" }} />}
      <Typography sx={{ fontSize: "14px", color: "#212121", fontWeight: 500 }}>
        {value}
      </Typography>
      {locked && (
        <LockOutlinedIcon sx={{ fontSize: 14, color: "#BDBDBD", ml: 0.5 }} />
      )}
    </Box>
  </Box>
);

const renderStatusBadge = (status: string) => {
  const statusStyles: Record<string, { text: string; bg: string }> = {
    Active: { text: "#2E7D32", bg: "#DBEFDC" },
    Inactive: { text: "#757575", bg: "#ECECEC" },
    "In active": { text: "#757575", bg: "#ECECEC" },
    Deactivated: { text: "#B3261D", bg: "#F0D4D2" },
    Pending: { text: "#9F7803", bg: "#FFF3CD" },
  };
  const defaultStyle = { text: "#374151", bg: "#f3f4f6" };
  const style = statusStyles[status] || defaultStyle;
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        px: 1.5,
        py: 0.5,
        bgcolor: style.bg,
        color: style.text,
        padding: "4px 10px",
        borderRadius: "6px",
        fontSize: "12px",
        fontWeight: 600,
      }}
    >
      <Box
        component="span"
        sx={{
          display: "inline-block",
          width: 4,
          height: 4,
          borderRadius: "50%",
          bgcolor: style.text,
        }}
      />
      {status}
    </Box>
  );
};

export const DetailsMember: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [showAlert, setShowAlert] = useState(true);
  const [deactivateOpen, setDeactivateOpen] = useState(false);

  const breadcrumbData = [
    { label: "SPOC Members", link: "/spoc-members" },
    { label: "SPOC Profile", link: "#" },
  ];

  return (
    <Box className="app-details-wrapper login-root">
      <div className="page-content">
        <GenericBreadCrums breadcrumbs={breadcrumbData} className="mt-6" />

        <Box className="app-details-card">
          {/* Banner */}
          <Box className="application-banner">
            {/* Banner Header */}
            <Box
              className="application-banner-header"
              sx={{ gap: { xs: 2, sm: 0 } }}
            >
              <Typography className="application-id flex gap-4 items-center">
                SPOC Profile

              </Typography>

              <Box
                className="banner-buttons"
                sx={{
                  display: "flex",
                  gap: "10px",
                  flexWrap: "wrap",
                  justifyContent: { xs: "flex-start" },
                }}
              >
                <Button
                  variant="outlined"
                  className="btn-outline-purple"
                  startIcon={<EditOutlinedIcon />}
                  onClick={() => navigate("/org-admin/spoc-members/edit/1")}
                  sx={{
                    border: "1px solid #3a1c73 !important",
                    borderRadius: "50px !important",
                    background: "#fff",
                    textTransform: "none",
                    fontWeight: 500,
                    fontSize: "14px",
                    color: "#3a1c73",
                  }}
                >
                  Edit Details
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<BlockOutlinedIcon />}
                  onClick={() => setDeactivateOpen(true)}
                  sx={{
                    borderRadius: "50px",
                    border: "1px solid #B3261D",
                    fontSize: "14px",
                    color: "#B3261D",
                    fontWeight: 500,
                    px: 2,
                    py: 0.5,
                    background: "#fff",
                    boxShadow: "none",
                    textTransform: "none",
                    height: "40px",
                  }}
                >
                  Deactivate
                </Button>
              </Box>
            </Box>

            {/* Sub-info bar */}
            <Box
              className="application-subinfo"
              sx={{
                flexWrap: "wrap",
                gap: { xs: 1, md: 2 },
                mt: { xs: 2, md: 0 },
              }}
            >
              <Typography className="app-date">
                <strong>View and manage SPOC details
</strong>
              </Typography>

            </Box>



            <br />
            <Divider className="app-divider" />
            <br />

            {/* Avatar summary bar */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                p: 2,
                bgcolor: "#F5F0FF",
                borderRadius: "12px",
                mb: 1,
              }}
            >
              <Avatar
                sx={{
                  width: 56,
                  height: 56,
                  bgcolor: "#3A1C73",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "20px",
                }}
              >
                {member.initials}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 700, fontSize: "18px", color: "#212121" }}>
                  {member.name}
                </Typography>
                <Typography sx={{ fontSize: "13px", color: "#757575", mb: 0.8 }}>
                  {member.designation}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  {renderStatusBadge(member.status)}
                  <Chip
                    label={member.assignedRole}
                    size="small"
                    sx={{
                      bgcolor: "#EDE7F6",
                      color: "#3A1C73",
                      fontWeight: 500,
                      fontSize: "12px",
                      height: "22px",
                    }}
                  />
                  <Typography sx={{ fontSize: "13px", color: "#757575" }}>
                    ID: {member.spocId}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Main two-column grid */}
          <Grid container spacing={4} justifyContent="center">
            {/* Left column */}
            <Grid item xs={12} md={8}>
              <Box className="app-section-header" mt={2}>
                <Box className="app-section-title">
                  <Typography className="section-heading">
                    SPOC Profile Details
                  </Typography>
                </Box>
              </Box>

              <Card
                className="app-main-content"
                sx={{ borderRadius: "12px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", mt: 2 }}
              >
                <CardContent sx={{ p: 3 }}>
                  {/* Tabs */}
                  <Tabs
                    value={activeTab}
                    onChange={(_, val) => setActiveTab(val)}
                    sx={{
                      mb: 3,
                      "& .MuiTab-root": {
                        textTransform: "none",
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "#757575",
                        minWidth: "auto",
                        px: 0,
                        mr: 3,
                      },
                      "& .Mui-selected": { color: "#3A1C73 !important", fontWeight: 600 },
                      "& .MuiTabs-indicator": { bgcolor: "#3A1C73 !important" },
                      borderBottom: "1px solid #E0E0E0",
                    }}
                  >
                    <Tab label="Personal Details" />
                    <Tab label="Activity" />
                    <Tab label="Audit Log" />
                  </Tabs>

                  {activeTab === 0 && (
                    <Box>
                      {/* Name & Designation */}
                      <Typography
                        sx={{ fontWeight: 700, fontSize: "15px", color: "#212121", mb: 2 }}
                      >
                        Basic Information
                      </Typography>
                      <Grid container spacing={3} sx={{ mb: 2.5 }}>
                        <Grid item xs={12} sm={6}>
                          <InfoField label="First Name" value={member.firstName} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <InfoField label="Last Name" value={member.lastName} />
                        </Grid>
                        <Grid item xs={12}>
                          <InfoField label="Designation" value={member.designation} />
                        </Grid>
                      </Grid>

                      <Divider sx={{ my: 2.5 }} />

                      {/* Contact Details */}
                      <Typography
                        sx={{ fontWeight: 700, fontSize: "15px", color: "#212121", mb: 2 }}
                      >
                        Contact Details
                      </Typography>
                      <Grid container spacing={3} sx={{ mb: 2.5 }}>
                        <Grid item xs={12} sm={6}>
                          <InfoField label="Email ID" value={member.email} isEmail locked />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <InfoField label="Mobile Number" value={member.phone} locked />
                        </Grid>
                      </Grid>

                      <Divider sx={{ my: 2.5 }} />

                      {/* Organisation Info */}
                      <Typography
                        sx={{ fontWeight: 700, fontSize: "15px", color: "#212121", mb: 2 }}
                      >
                        Organisation Info
                      </Typography>
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                          <InfoField label="Organisation" value={member.organisation} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <InfoField label="Onboarded On" value={member.onboardedDate} />
                        </Grid>
                      </Grid>
                    </Box>
                  )}

                  {activeTab === 1 && (
                    <Typography
                      sx={{ color: "#9E9E9E", fontSize: "14px", py: 4, textAlign: "center" }}
                    >
                      No activity records found.
                    </Typography>
                  )}

                  {activeTab === 2 && (
                    <Typography
                      sx={{ color: "#9E9E9E", fontSize: "14px", py: 4, textAlign: "center" }}
                    >
                      No audit log entries found.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Right sidebar */}
            <Grid item xs={12} md={4}>
              {/* Role & Permissions */}
              <Card
                sx={{
                  borderRadius: "12px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  mb: 2.5,
                  mt: { xs: 0, md: 7 },
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Typography
                    sx={{ fontWeight: 700, fontSize: "15px", color: "#212121", mb: 2 }}
                  >
                    Role &amp; Permissions
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: "#757575", mb: 1 }}>
                    Assigned Role
                  </Typography>
                  <Chip
                    label={member.assignedRole}
                    size="small"
                    sx={{
                      bgcolor: "#EDE7F6",
                      color: "#3A1C73",
                      fontWeight: 500,
                      fontSize: "12px",
                      mb: 2,
                    }}
                  />
                  <Divider sx={{ mb: 2 }} />
                  <Typography sx={{ fontSize: "13px", color: "#424242", lineHeight: 1.6 }}>
                    Can view and verify applications. Cannot approve or create.
                  </Typography>
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <Card sx={{ borderRadius: "12px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Typography
                    sx={{ fontWeight: 700, fontSize: "15px", color: "#212121", mb: 2 }}
                  >
                    Quick Actions
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    <Button
                      variant="contained"
                      className="btn-filled-purple"
                      startIcon={<EditOutlinedIcon />}
                      onClick={() => navigate("/org-admin/spoc-members/edit/1")}
                      fullWidth
                      sx={{ borderRadius: "50px !important", textTransform: "none", justifyContent: "flex-start", px: 2 }}
                    >
                      Edit Details
                    </Button>
                    {["Change Role"].map((action) => (
                      <Button
                        key={action}
                        variant="outlined"
                        fullWidth
                        sx={{
                          textTransform: "none",
                          fontWeight: 500,
                          fontSize: "14px",
                          py: 1,
                          borderRadius: "50px",
                          borderColor: "#3a1c73",
                          color: "#3a1c73",
                          justifyContent: "flex-start",
                          px: 2,
                          "&:hover": { borderColor: "#3a1c73", bgcolor: "#F5F0FF" },
                        }}
                      >
                        {action}
                      </Button>
                    ))}
                    <Button
                      variant="outlined"
                      startIcon={<BlockOutlinedIcon />}
                      onClick={() => setDeactivateOpen(true)}
                      fullWidth
                      sx={{
                        textTransform: "none",
                        fontWeight: 500,
                        fontSize: "14px",
                        py: 1,
                        borderRadius: "50px",
                        borderColor: "#B3261D",
                        color: "#B3261D",
                        justifyContent: "flex-start",
                        px: 2,
                        "&:hover": { borderColor: "#B3261D", bgcolor: "#FFF0EE" },
                      }}
                    >
                      Deactivate SPOC
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </div>

      {/* Deactivate Confirmation Modal */}
      <Dialog
        open={deactivateOpen}
        onClose={() => setDeactivateOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: "15px",
            maxWidth: "500px",
            width: "100%",
          },
        }}
      >
        <DialogContent sx={{ textAlign: "left", px: 4, py: 3 }}>
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: "50%",
              bgcolor: "#FDE8E8",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 2,
              border: "1px solid #F5C6C6",
            }}
          >
            <Typography sx={{ fontSize: "22px", color: "#B3261D", fontWeight: 700, lineHeight: 1 }}>
              !
            </Typography>
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: "18px", color: "#212121", mb: 1.5 }}>
            Deactivate SPOC?
          </Typography>
          <Typography sx={{ fontSize: "14px", color: "#424242", lineHeight: 1.7, mb: 3 }}>
            Are you sure you want to deactivate{" "}
            <Box component="span" sx={{ fontWeight: 700 }}>
              {member.name}
            </Box>
            ? They will immediately lose access to the system and cannot perform
            any actions until reactivated.
          </Typography>
          <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
            <Button
              variant="outlined"
              onClick={() => setDeactivateOpen(false)}
              sx={{
                borderRadius: "50px",
                textTransform: "none",
                fontWeight: 500,
                fontSize: "14px",
                px: 3,
                py: 1,
                borderColor: "#BDBDBD",
                color: "#424242",
                "&:hover": { borderColor: "#9E9E9E", bgcolor: "#F5F5F5" },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => setDeactivateOpen(false)}
              sx={{
                borderRadius: "50px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "14px",
                px: 3,
                py: 1,
                bgcolor: "#7B1C1C",
                color: "#fff",
                boxShadow: "none",
                "&:hover": { bgcolor: "#5C1414" },
              }}
            >
              Yes, Deactivate
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};
